import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Inline multilingual text field with a 3-way language toggle (PT|EN|ES).
 *
 * - `value` is the canonical PT value (stored at the document root).
 * - `translations` is an object `{ en: { fieldName: "..." }, es: {...} }`.
 * - When the admin types in EN/ES, we update `translations[lang][fieldName]`.
 * - When the admin types in PT, we update the root `value`.
 * - When in EN/ES mode and the field is empty, a "✨ Traduzir do PT" button
 *   calls Claude Sonnet 4.5 (via `/admin/i18n/translate`) and fills the field.
 *   The admin can edit before saving.
 */
export default function TranslatedInput({
  label,
  field,
  value,
  translations = {},
  onChange,
  placeholder,
  testid,
  className = "",
  compact = false,
  context = null,
}) {
  const [lang, setLang] = useState("pt");
  const [aiBusy, setAiBusy] = useState(false);

  const currentValue = lang === "pt" ? (value || "") : ((translations?.[lang]?.[field]) || "");

  const updateValue = (next) => {
    if (lang === "pt") {
      onChange({ value: next, translations });
    } else {
      const nextTr = { ...(translations || {}) };
      nextTr[lang] = { ...(nextTr[lang] || {}), [field]: next };
      onChange({ value, translations: nextTr });
    }
  };

  // Visual cue: badge shows if the active non-PT language has a value yet.
  const hasOverride = (l) => l !== "pt" && !!translations?.[l]?.[field];

  const handleAutoTranslate = async () => {
    if (lang === "pt" || !value || aiBusy) return;
    setAiBusy(true);
    try {
      const r = await api.post("/admin/i18n/translate", {
        text: value,
        target_lang: lang,
        context: context || undefined,
      });
      const translated = (r.data?.translated || "").trim();
      if (translated) {
        updateValue(translated);
        toast.success(`Sugestão ${lang.toUpperCase()} aplicada — edita se precisares antes de guardar`);
      } else {
        toast.error("Tradução vazia retornada pelo modelo");
      }
    } catch (e) {
      toast.error("Tradução falhou: " + (e?.response?.data?.detail || e.message));
    } finally {
      setAiBusy(false);
    }
  };

  const toggleEl = (
    <span className="inline-flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5" data-testid={`${testid}-lang-toggle`}>
      {[
        { code: "pt", flag: "🇵🇹" },
        { code: "en", flag: "🇬🇧" },
        { code: "es", flag: "🇪🇸" },
      ].map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setLang(l.code)}
          className={`relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold transition-colors ${
            lang === l.code ? "bg-white shadow-sm text-violet-700" : "text-slate-500 hover:text-slate-800"
          }`}
          data-testid={`${testid}-lang-${l.code}`}
          title={l.code.toUpperCase()}
        >
          <span>{l.flag}</span>
          <span>{l.code.toUpperCase()}</span>
          {hasOverride(l.code) && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-500" />}
        </button>
      ))}
    </span>
  );

  // The "auto-translate" button appears only when we're in EN/ES mode AND
  // we have a PT canonical value to translate from. It's harmless when the
  // EN/ES field already has a value (the admin can still re-trigger).
  const aiButton = lang !== "pt" && (value || "").trim() ? (
    <button
      type="button"
      onClick={handleAutoTranslate}
      disabled={aiBusy}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-sm hover:from-violet-600 hover:to-fuchsia-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
      data-testid={`${testid}-ai-translate`}
      title={`Auto-traduzir do PT com IA (Claude)`}
    >
      {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      <span>{aiBusy ? "A traduzir…" : `Traduzir do PT`}</span>
    </button>
  ) : null;

  if (compact) {
    // Inline mode for tight layouts (table rows). Lang toggle sits to the
    // right of the input on the same row — no Label rendered.
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <Input
          value={currentValue}
          onChange={(e) => updateValue(e.target.value)}
          placeholder={lang === "pt" ? placeholder : (lang === "en" ? "(optional)" : "(opcional)")}
          data-testid={testid}
        />
        {aiButton}
        {toggleEl}
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <div className="flex items-center justify-between mb-1 gap-2">
          <Label>{label}</Label>
          <div className="flex items-center gap-2">
            {aiButton}
            {toggleEl}
          </div>
        </div>
      )}
      {!label && (
        <div className="flex justify-end mb-1 gap-2">
          {aiButton}
          {toggleEl}
        </div>
      )}
      <Input
        value={currentValue}
        onChange={(e) => updateValue(e.target.value)}
        placeholder={lang === "pt"
          ? placeholder
          : (lang === "en"
            ? "(optional — leave empty to use the PT value)"
            : "(opcional — si está vacío usa el valor PT)")}
        data-testid={testid}
      />
      {lang !== "pt" && !currentValue && (
        <p className="text-[10px] text-slate-500 mt-1 italic">Sem tradução em {lang.toUpperCase()} — utilizadores deste idioma verão o valor PT.</p>
      )}
    </div>
  );
}
